import {
  BonificaDto,
  BonificaImmagineDto,
  getDownloadBonificaImmagineQueryKey,
  getDownloadFileQueryKey,
  getDownloadRapportinoBonificaQueryKey,
  getGetNodoPortaleQueryKey,
  getGetNodoQueryKey,
  NodoDto,
  useUpdateBonifiche,
} from "@/api";
import Box from "@/elements/Box";
import Typography from "@/elements/Typography";
import Card from "@mui/material/Card";
import Table from "@/elements/Table";
import moment from "moment/moment";
import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { Fragment, useCallback, useMemo, useState } from "react";
import Button from "@/elements/Button";
import { Tooltip } from "@mui/material";
import { useTranslation } from "react-i18next";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import BonificaDialog from "@/components/BonificaDialog";
import FileDownload from "@/components/FileField/FileDownload";
import { toast } from "react-toastify";
import { useQueryClient } from "@tanstack/react-query";
import Image from "../Images/Image";
import { portaleConfig } from "@/portaliConfig";

const columnHelper = createColumnHelper<BonificaDto>();

export default function BonificheTableCard({
  nodo,
  bonifiche,
  canEdit = false,
}: {
  nodo: NodoDto;
  bonifiche: BonificaDto[];
  canEdit?: boolean;
}) {
  const { t } = useTranslation();

  const [bonificaDialogOptions, setBonificaDialogOptions] = useState<{
    open: boolean;
    bonifica?: BonificaDto;
  }>({ open: false });

  const editing = canEdit;

  const queryClient = useQueryClient();
  const { mutate } = useUpdateBonifiche();

  const deleteBonifica = useCallback(
    (bonifica: BonificaDto) => {
      const data = moment(bonifica.data).format("L");
      const message = t("bonificheTable.confermaElimina", {
        data,
        descrizione: bonifica.descrizione,
      });
      if (!window.confirm(message)) {
        return;
      }

      mutate(
        {
          id: nodo.id,
          data: [
            {
              id: bonifica.id,
              changeType: "deleted",
              entity: {
                ...bonifica,
                bonificaImmagini: [],
              },
            },
          ],
        },
        {
          onSuccess: () => {
            queryClient
              .invalidateQueries(
                getGetNodoPortaleQueryKey(portaleConfig.id, nodo.id)
              )
              .then(() => {
                toast.success(t("bonificheTable.toast.eliminata"));
              });
          },
          onError: () => {
            toast.error(t("bonificheTable.toast.eliminazioneErrore"));
          },
        }
      );
    },
    [queryClient, nodo.id, mutate, t]
  );

  const columns = useMemo<ColumnDef<BonificaDto, any>[]>(() => {
    const columns = [
      columnHelper.accessor("data", {
        header: t("bonificheTable.colonne.data"),
        cell: ({ row }) => moment(row.original.data).format("L"),
      }),

      columnHelper.accessor("scadenza", {
        header: t("bonificheTable.colonne.scadenza"),
        cell: ({ row }) =>
          row.original.scadenza && moment(row.original.scadenza).format("L"),
      }),

      columnHelper.accessor("descrizione", {
        header: t("bonificheTable.colonne.descrizione"),
        minSize: 200,
      }),

      columnHelper.accessor("bonificaImmagini", {
        header: t("bonificheTable.colonne.immagini"),
        minSize: 200,
        cell: ({ row }) => {
          if (row.original.bonificaImmagini?.length === 0) {
            return t("bonificheTable.immaginiVuote");
          } else {
            return (
              <Card
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 1,
                  borderRadius: 0,
                }}
              >
                {row.original.bonificaImmagini.map(
                  (immagine: BonificaImmagineDto, i: number) => {
                    const saved = !!row.original.id && !!immagine.id;
                    const justUploaded = !!immagine.uploadedFileId;

                    const url = saved
                      ? getDownloadBonificaImmagineQueryKey(
                          nodo.id,
                          row.original.id,
                          immagine.id!
                        )[0]
                      : justUploaded
                      ? getDownloadFileQueryKey(immagine.uploadedFileId!)[0]
                      : null;

                    if (!url) {
                      return <Fragment key={immagine.id} />;
                    }

                    return (
                      <Image
                        key={immagine.id ?? i}
                        src={url}
                        alt={t("bonificheTable.altImmagine", {
                          index: i + 1,
                        })}
                        canDelete={false}
                      />
                    );
                  }
                )}
              </Card>
            );
          }
        },
      }),

      columnHelper.display({
        header: t("bonificheTable.colonne.rapportino"),
        cell: ({ row }) => (
          <FileDownload
            file={row.original.fileRapportino}
            downloadUrl={
              getDownloadRapportinoBonificaQueryKey(nodo.id, row.original.id)[0]
            }
          />
        ),
      }),

      editing &&
        columnHelper.display({
          id: "buttons",
          header: "",
          size: 100,
          maxSize: 100,
          cell: ({ row }) => {
            return (
              <Box
                display="flex"
                flexDirection="row"
                justifyContent="flex-end"
                gap={1}
              >
                <Tooltip title={t("azioni.elimina")}>
                  <IconButton
                    onClick={() => {
                      deleteBonifica(row.original);
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>

                <Tooltip title={t("azioni.modifica")}>
                  <IconButton
                    onClick={() => {
                      setBonificaDialogOptions({
                        open: true,
                        bonifica: row.original,
                      });
                    }}
                  >
                    <EditIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            );
          },
        }),
    ].filter((x) => x);
    return columns as Exclude<(typeof columns)[number], boolean>[];
  }, [editing, nodo.id, deleteBonifica, t]);

  return (
    <>
      <Box flex={1}>
        <Box display="flex" flexDirection="column" height="100%" gap={1}>
          <Box
            display="flex"
            flexDirection="row"
            justifyContent="space-between"
            gap={1}
          >
            <Typography variant="h5" color="text" fontWeight="medium">
              {t("bonificheTable.titolo")}
            </Typography>

            {editing && (
              <Button
                color="primary"
                size="small"
                onClick={() => {
                  setBonificaDialogOptions({ open: true });
                }}
              >
                {t("azioni.aggiungi")}
              </Button>
            )}
          </Box>

          <Table
            columns={columns}
            data={bonifiche}
            initialSort={[{ id: "data", desc: true }]}
            noRowsText={t("bonificheTable.noRows")}
            mt={1}
          />
        </Box>
      </Box>

      <BonificaDialog
        nodo={nodo}
        open={bonificaDialogOptions.open}
        bonifica={bonificaDialogOptions.bonifica}
        onClose={() => {
          setBonificaDialogOptions({ open: false });
        }}
      />
    </>
  );
}
